import * as React from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Container,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Grid,
  Collapse,
  Divider,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation } from 'react-router-dom';
import { Logo } from './Logo';
import useMediaQuery from '@mui/material/useMediaQuery';
 
/*
 * NavBar
 * ------
 * Top-level navigation component used across the site. It supports:
 * - Responsive layout (desktop horizontal menu, mobile drawer)
 * - Dropdown submenus for pages that expose multiple sections
 * - Navigation via react-router's 'navigate'
 * - Smooth scrolling to anchored sections when navigating to a sub-item
 */

function NavBar() {
  const pages = ['Home', 'About', 'Schedule', 'Memberships', 'Gallery', 'Contact Us'];

  // Submenu items for selected pages. Keys match entries in 'pages'.
  const dropdownItems = {
    About: ['Our Coaches', 'Our Facility'],
    Schedule: ['Classes Offered', 'Our Schedule'],
    Memberships: [
      'Membership Plans',
      '3-Day Free Trial',
      'Required Equipment',
      'Hygiene & Etiquette',
      'Private Training & Drop-ins',
    ],
  };

  // mobileOpen: whether the left-side drawer (mobile menu) is visible
  // openDropdown: which mobile submenu (if any) is currently expanded
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [openDropdown, setOpenDropdown] = React.useState(null);

  // Keep track of any active timeouts so they can be cleared on unmount.
  const timeoutRef = React.useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  // hide the Join Now button for smaller widths to avoid pushing the nav to two rows
  const showJoin = useMediaQuery(theme.breakpoints.up('lg'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Prevent background scrolling when mobile drawer is open
  React.useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : 'unset';
  }, [mobileOpen]);

  // Clear any pending timeouts when the component unmounts.
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);


  const routeChange = (page) => {
    const path = page.replace(/ /g, '-').toLowerCase();
    const sectionRouteMap = {
      'our-coaches': '/about',
      'our-facility': '/about',
      'classes-offered': '/schedule',
      'our-schedule': '/schedule',
      'membership-plans': '/memberships',
      '3-day-free-trial': '/memberships',
      'required-equipment': '/memberships',
      'hygiene-&-etiquette': '/memberships',
      'private-training-&-drop-ins': '/memberships',
    };

    // Special case: navigate Home to root path
    if (path === 'home') {
      navigate('/');
      setMobileOpen(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const routePath = sectionRouteMap[path] || `/${path}`;
    if (!sectionRouteMap[path]) {
      navigate(routePath);
      setMobileOpen(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    navigate(routePath + `#${path}`);
    setMobileOpen(false);


    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      const section = document.getElementById(path);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
      timeoutRef.current = null;
    }, 500);
  };

  const drawer = (
    <Box role="navigation" sx={{ textAlign: 'left', pt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
        <Box onClick={() => routeChange('Home')} sx={{ cursor: 'pointer' }}>
          <Logo />
        </Box>
        <IconButton onClick={handleDrawerToggle} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <List>
        {pages.map((page) => {
          const subItems = dropdownItems[page];

          return (
            <Box key={page}>
              <ListItem
                button
                // If the page has submenu items, toggle expansion; otherwise navigate
                onClick={() =>
                  subItems ? setOpenDropdown(openDropdown === page ? null : page) : routeChange(page)
                }
                sx={{
                  justifyContent: 'flex-start',
                  py: 1.5,
                  pl: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.08)',
                  },
                }}
              >
                <ListItemText
                  primary={page}
                  primaryTypographyProps={{
                    fontWeight: 'bold',
                    color: 'white',
                  }}
                />
              </ListItem>

              {/* Expandable submenu for this page in the mobile drawer */}
              <Collapse in={openDropdown === page} timeout="auto" unmountOnExit>
                {subItems?.map((sub) => (
                  <ListItem
                    key={sub}
                    button
                    onClick={() => routeChange(sub)}
                    sx={{
                      justifyContent: 'flex-start',
                      py: 1.2,
                      pl: 5,
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.05)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={sub}
                      primaryTypographyProps={{
                        color: 'white',
                        fontSize: '0.85rem',
                        fontWeight: 400,
                        sx: { opacity: 0.8 },
                      }}
                    />
                  </ListItem>
                ))}
              </Collapse>

              <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)', my: 1 }} />
            </Box>
          );
        })}
      </List>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      sx={{
        background: 'linear-gradient(to bottom, #111, #1a1a1a)',
        backdropFilter: 'blur(6px)',
        boxShadow: 'none',
        fontFamily: 'Oswald, sans-serif',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Mobile Nav */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, width: '100%' }}>
            <Grid container alignItems="center" sx={{ width: '100%' }}>
              <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <IconButton
                  color="inherit"
                  aria-label="open navigation drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                >
                  <MenuIcon />
                </IconButton>
              </Grid>
                  <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                    {/* Centered logo for mobile */}
                    <Box onClick={() => routeChange('Home')} sx={{ cursor: 'pointer' }}>
                      <Logo width={200} />
                    </Box>
                  </Grid>
              <Grid item xs={3} />
            </Grid>
          </Box>

          {/* Desktop Nav */}
          <Grid container alignItems="center" sx={{ display: { xs: 'none', md: 'flex' }, width: '100%' }}>
            <Grid item>
              <Box onClick={() => routeChange('Home')} sx={{ cursor: 'pointer', mr: theme.spacing(2) }}>
                <Logo />
              </Box>
            </Grid>

            <Grid item sx={{ flexGrow: 1 }}>
              {/* Keep nav items on a single line; allow overflow to be visible
                  so absolutely-positioned dropdown menus can extend outside
                  the nav container and be visible on hover. */}
              <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'nowrap', overflow: 'visible' }}>
                {pages.map((page) => {
                  const active = location.pathname.includes(page.toLowerCase());
                  const subItems = dropdownItems[page];

                  return (
                    <Box
                      key={page}
                      sx={{
                        position: 'relative',
                        mx: theme.spacing(1),
                        '&:hover .dropdown': {
                          display: 'block',
                        },
                      }}
                    >
                      <Button
                        onClick={() => routeChange(page)}
                        sx={{
                          color: 'white',
                          textTransform: 'uppercase',
                          fontWeight: active ? 'bold' : 'normal',
                          transition: 'transform 0.2s, background 0.2s',
                          transform: active ? 'scale(1.05)' : undefined,
                          backgroundColor: active ? 'rgba(255,255,255,0.1)' : undefined,
                          '&:hover': {
                            transform: 'scale(1.05)',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                          },
                        }}
                      >
                        {page}
                      </Button>

                      {subItems && (
                        <Box
                          className="dropdown"
                          sx={{
                            display: 'none',
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            backgroundColor: '#111',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                            zIndex: 10,
                            minWidth: '200px',
                          }}
                        >
                          {subItems.map((sub) => (
                            <Button
                              key={sub}
                              onClick={() => routeChange(sub)}
                              sx={{
                                display: 'block',
                                width: '100%',
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                                color: 'white',
                                px: 2,
                                py: 1,
                                textTransform: 'none',
                                fontSize: '0.95rem',
                                '&:hover': {
                                  backgroundColor: 'rgba(255,255,255,0.08)',
                                },
                              }}
                            >
                              {sub}
                            </Button>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Grid>

            {/* Conditionally render the Join CTA only when there's enough
                horizontal space (large screens and up) */}
            {showJoin && (
              <Grid item>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => routeChange('Memberships')}
                  sx={{
                    fontWeight: 'bold',
                    boxShadow: 'none',
                    textTransform: 'uppercase',
                    '&:hover': {
                      backgroundColor: '#fff',
                      color: '#000',
                    },
                  }}
                >
                  Join Now
                </Button>
              </Grid>
            )}
          </Grid>
        </Toolbar>
      </Container>

      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: '80vw',
            backgroundColor: '#000',
            color: '#fff',
          },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
}

export default NavBar;
